import { useState, useCallback } from 'react'
import type { Agent } from '@/services/api/models/Agent'
import { AgentManagementService } from '@/services/api/services/AgentManagementService'
import type { PaginationInfo } from '@/services/api/models/PaginationInfo'
import { ApiError } from '@/services/api/core/ApiError'
import {
  type AgentEncryptionSettings,
  getAgentEncryptionSettings,
  saveAgentEncryptionSettings
} from '@/utils/agentEncryption'

export interface AgentFilters {
  search?: string
  status?: 'active' | 'inactive'
  connectionStatus?: 'connected' | 'disconnected' | 'error'
}

export interface AgentFormData {
  name: string
  description?: string
  is_active?: boolean
  capabilities?: Record<string, unknown>
  metadata?: Record<string, unknown>
  encryption?: AgentEncryptionSettings
}

export interface AgentUpdateData {
  name?: string
  description?: string
  is_active?: boolean
  capabilities?: Record<string, unknown>
  metadata?: Record<string, unknown>
  encryption?: AgentEncryptionSettings
}

export interface UseAgentsReturn {
  // Data
  agents: Agent[]
  pagination: PaginationInfo | null
  loading: boolean
  error: string | null

  // Actions
  fetchAgents: (page?: number, limit?: number, filters?: AgentFilters) => Promise<void>
  createAgent: (data: AgentFormData) => Promise<{ agent: Agent; apiKey: string } | null>
  updateAgent: (id: string, data: AgentUpdateData) => Promise<boolean>
  deleteAgent: (id: string) => Promise<boolean>
  getAgent: (id: string) => Promise<Agent | null>
  
  // Encryption management
  getAgentEncryption: (agentId: string) => AgentEncryptionSettings
  saveAgentEncryption: (agentId: string, settings: AgentEncryptionSettings) => void
  
  // Utility
  refreshAgents: (page?: number, limit?: number, filters?: AgentFilters) => Promise<void>
  clearError: () => void
}

// Simple cache to prevent duplicate API calls across components
let agentCache: { data: Agent[]; timestamp: number } | null = null
const CACHE_DURATION = 30000 // 30 seconds

// Request deduplication to prevent concurrent identical requests
let pendingRequest: Promise<unknown> | null = null

export function useAgents(): UseAgentsReturn {
  const [agents, setAgents] = useState<Agent[]>([])
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentLimit, setCurrentLimit] = useState(20)
  const [currentFilters, setCurrentFilters] = useState<AgentFilters>({})

  const handleError = useCallback((err: unknown) => {
    console.error('Agent operation error:', err)
    if (err instanceof ApiError) {
      setError(err.message || 'An API error occurred')
    } else if (err instanceof Error) {
      setError(err.message)
    } else {
      setError('An unexpected error occurred')
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const fetchAgents = useCallback(async (
    page = 1,
    limit = 20,
    filters: AgentFilters = {},
    isRefresh = false
  ) => {
    // Check cache for basic agent list (page 1, no filters)
    const isBasicRequest = page === 1 && limit === 20 && Object.keys(filters).length === 0
    if (!isRefresh && isBasicRequest && agentCache && Date.now() - agentCache.timestamp < CACHE_DURATION) {
      setAgents(agentCache.data)
      setCurrentPage(page)
      setCurrentLimit(limit)
      setCurrentFilters(filters)
      return
    }

    // If there's already a pending request for the same parameters, wait for it
    if (!isRefresh && isBasicRequest && pendingRequest) {
      try {
        const response = await pendingRequest
        const candidate: unknown = response
        const agentData = (candidate && typeof candidate === 'object' && 'data' in (candidate as Record<string, unknown>))
          ? (((candidate as { data?: unknown }).data && Array.isArray((candidate as { data?: unknown }).data))
              ? ((candidate as { data?: Agent[] }).data ?? [])
              : [])
          : []
        setAgents(agentData)
        setCurrentPage(page)
        setCurrentLimit(limit)
        setCurrentFilters(filters)
        return
      } catch {
        // If pending request failed, continue with new request
      }
    }

    try {
      // Don't show loading state for refresh to prevent UI flicker
      if (!isRefresh) {
        setLoading(true)
      }
      setError(null)
      
      const requestPromise = AgentManagementService.getAgents({
        page,
        limit,
        search: filters.search,
        status: filters.status,
        connectionStatus: filters.connectionStatus
      })

      // Store the request promise for deduplication
      if (isBasicRequest) {
        pendingRequest = requestPromise
      }

      const response = await requestPromise

      const agentData = response.data || []
      
      // Smart merge: only update agents that have actually changed
      if (isRefresh) {
        setAgents(prevAgents => {
          // If this is page 1, we should replace all agents 
          // (as page 1 represents the latest state)
          // Always create new objects to ensure React detects the change
          if (page === 1) {
            // Create new object instances to force React to detect changes
            return agentData.map(agent => ({ ...agent }))
          }
          
          // For other pages during refresh, we need to merge carefully
          // Create a map of new agents for quick lookup
          const newAgentsMap = new Map(agentData.map(agent => [agent.id, agent]))
          
          // Update existing agents that are in the current page response
          const updatedAgents = prevAgents.map(prevAgent => {
            const newAgent = newAgentsMap.get(prevAgent.id)
            if (newAgent) {
              // Always return a new object instance to ensure React detects changes
              // This is important for status updates and other property changes
              newAgentsMap.delete(prevAgent.id) // Remove from map as it's been processed
              return { ...newAgent }
            }
            // Keep agents that aren't in the current page response unchanged
            return prevAgent
          })
          
          // Add any new agents that weren't in the previous list
          const newAgents = Array.from(newAgentsMap.values()).map(agent => ({ ...agent }))
          
          return [...updatedAgents, ...newAgents]
        })
      } else {
        setAgents(agentData)
      }
      
      setPagination(response.pagination || null)
      setCurrentPage(page)
      setCurrentLimit(limit)
      setCurrentFilters(filters)

      // Update cache - for refresh, always update cache to ensure fresh data propagates
      if (isBasicRequest || isRefresh) {
        agentCache = { data: agentData, timestamp: Date.now() }
        pendingRequest = null
      }
    } catch (err) {
      handleError(err)
      if (!isRefresh) {
        setAgents([])
      }
      setPagination(null)
      // Clear pending request on error
      if (isBasicRequest) {
        pendingRequest = null
      }
    } finally {
      if (!isRefresh) {
        setLoading(false)
      }
    }
  }, [handleError])

  const createAgent = useCallback(async (data: AgentFormData) => {
    try {
      setError(null)
      
      // Separate encryption settings from API data
      const { encryption, ...apiData } = data
      
      const response = await AgentManagementService.createAgents({
        requestBody: apiData
      })
      
      if (response.agent) {
        // Save encryption settings to localStorage if provided
        if (encryption && response.agent.id) {
          saveAgentEncryptionSettings(response.agent.id, encryption)
        }
        
        // Invalidate cache and pending request, then refresh the agents list to include the new agent
        agentCache = null
        pendingRequest = null
        await fetchAgents(currentPage, currentLimit, currentFilters)
        return {
          agent: response.agent,
          apiKey: response.apiKey || ''
        }
      }
      return null
    } catch (err) {
      handleError(err)
      return null
    }
  }, [fetchAgents, currentPage, currentLimit, currentFilters, handleError])

  const updateAgent = useCallback(async (id: string, data: AgentUpdateData) => {
    try {
      setError(null)
      
      // Separate encryption settings from API data
      const { encryption, ...apiData } = data
      
      await AgentManagementService.updateAgents({
        agentId: id,
        requestBody: apiData
      })
      
      // Save encryption settings to localStorage if provided
      if (encryption) {
        saveAgentEncryptionSettings(id, encryption)
      }
      
      // Invalidate cache and pending request, then update the agent in the current list
      agentCache = null
      pendingRequest = null
      setAgents(prev => prev.map(agent => 
        agent.id === id ? { ...agent, ...apiData, updated_at: new Date().toISOString() } : agent
      ))
      
      return true
    } catch (err) {
      handleError(err)
      return false
    }
  }, [handleError])

  const deleteAgent = useCallback(async (id: string) => {
    try {
      setError(null)
      await AgentManagementService.deleteAgents({
        agentId: id
      })
      
      // Note: We don't remove encryption settings from localStorage - they persist forever
      // This allows users to maintain their encryption settings even if agents are recreated
      
      // Invalidate cache and pending request, then remove the agent from the current list
      agentCache = null
      pendingRequest = null
      setAgents(prev => prev.filter(agent => agent.id !== id))
      
      return true
    } catch (err) {
      handleError(err)
      return false
    }
  }, [handleError])

  const getAgent = useCallback(async (id: string) => {
    try {
      setError(null)
      return await AgentManagementService.getAgents1({
        agentId: id
      })
    } catch (err) {
      handleError(err)
      return null
    }
  }, [handleError])

  const refreshAgents = useCallback(async (page?: number, limit?: number, filters?: AgentFilters) => {
    // Don't clear cache for refresh - use smart merge instead
    await fetchAgents(
      page ?? currentPage, 
      limit ?? currentLimit, 
      filters ?? currentFilters,
      true // isRefresh flag
    )
  }, [fetchAgents, currentPage, currentLimit, currentFilters])

  // Encryption management functions
  const getAgentEncryption = useCallback((agentId: string) => {
    return getAgentEncryptionSettings(agentId)
  }, [])

  const saveAgentEncryption = useCallback((agentId: string, settings: AgentEncryptionSettings) => {
    saveAgentEncryptionSettings(agentId, settings)
  }, [])

  // Note: No automatic fetching - components control when to fetch
  // This prevents duplicate requests from multiple hook instances

  return {
    // Data
    agents,
    pagination,
    loading,
    error,

    // Actions
    fetchAgents,
    createAgent,
    updateAgent,
    deleteAgent,
    getAgent,
    
    // Encryption management
    getAgentEncryption,
    saveAgentEncryption,
    
    // Utility
    refreshAgents,
    clearError,
  }
}
